import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { readCache, writeCache, isMeaningfulResponse } from "@/lib/persistCache";

const SettingsContext = createContext({ settings: null });

export function SettingsProvider({ children }) {
  // Hydrate INSTANTLY from cached settings if available — eliminates the
  // "blank page during pod restart" UX where bio, tagline, contacts vanish
  // while the backend is reconnecting. Cached snapshot stays on screen until
  // a meaningful fresh response replaces it.
  const [settings, setSettings] = useState(() => readCache("settings"));

  const refresh = async () => {
    try {
      const { data } = await api.get("/settings");
      if (isMeaningfulResponse(data)) {
        setSettings(data);
        writeCache("settings", data);
      }
      // If response is empty/garbage, keep the cached snapshot in state.
    } catch (e) {
      // Network error or 5xx during backend cold-start: keep the cache.
      // eslint-disable-next-line no-console
      console.error("Failed to load settings (using cache fallback):", e?.message || e);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);

/**
 * Build a robust WhatsApp deep-link.
 *
 * Uses api.whatsapp.com/send which is the most reliable endpoint across:
 *  - Android Chrome (opens WhatsApp app or Play Store)
 *  - iOS Safari (opens WhatsApp app, falls back to web)
 *  - Desktop (opens WhatsApp Web, then desktop app)
 *
 * Accepts numbers in any common format and normalises to E.164 digits-only.
 */
export function buildWhatsAppLink(number, message) {
  if (!number) return "#";
  // Strip every non-digit; rely on stored E.164 already including country code.
  let cleaned = String(number).replace(/[^\d]/g, "");
  // Defensive: WhatsApp expects 8-15 digits (ITU E.164)
  if (cleaned.length < 8 || cleaned.length > 15) return "#";
  const text = encodeURIComponent(message || "Hi BuildDirect Africa, I'd like to discuss a project.");
  return `https://wa.me/${cleaned}?text=${text}`;
}

const isMobileUA = () => {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
};

/**
 * Convert a wa.me URL into the native whatsapp:// URL scheme.
 * Returns null on any parse failure.
 */
function toNativeWhatsAppScheme(href) {
  try {
    const url = new URL(href);
    const phone = url.pathname.replace(/[^\d]/g, "");
    const text = url.searchParams.get("text") || "";
    if (!phone || phone.length < 8) return null;
    return `whatsapp://send?phone=${phone}&text=${encodeURIComponent(text)}`;
  } catch {
    return null;
  }
}

/**
 * Bullet-proof WhatsApp launcher.
 *
 * Strategy:
 *  • On MOBILE (Android Chrome, Samsung Internet, iOS Safari, and every
 *    in-app browser — Instagram, Facebook, Emergent preview), we trigger
 *    the native `whatsapp://send?phone=…` URL scheme **directly**. The
 *    OS-level WhatsApp handler intercepts it instantly — no browser
 *    intermediate page (so no "Failed to load" wrapper screen possible).
 *    Falls back to the wa.me web URL after 1.2 s if nothing happens (e.g.
 *    WhatsApp not installed).
 *  • On DESKTOP, opens wa.me in a new tab so the gallery stays open.
 *
 * No React Router, no relative URLs, no internal route handling.
 */
export function onWhatsAppClick(e) {
  const href = e?.currentTarget?.getAttribute("href") || "";
  if (!href || href === "#") return;
  e.preventDefault();
  e.stopPropagation();

  if (isMobileUA()) {
    // Direct OS handoff via the native WhatsApp URL scheme — no browser page.
    const native = toNativeWhatsAppScheme(href);
    if (native) {
      // Fallback chain in case WhatsApp isn't installed: after 1.2 s, try
      // wa.me. We use document.hidden to detect whether the app actually
      // launched (WhatsApp opening backgrounds our tab → document.hidden=true).
      const startTime = Date.now();
      const fallbackTimer = setTimeout(() => {
        if (Date.now() - startTime < 1500 && !document.hidden) {
          // App didn't launch — try the web fallback in a NEW tab so we
          // don't disrupt the gallery state with a same-tab navigation.
          const w = window.open(href, "_blank", "noopener,noreferrer");
          if (!w) {
            try {
              // eslint-disable-next-line no-restricted-globals
              (window.top || window).location.href = href;
            } catch {
              window.location.href = href;
            }
          }
        }
      }, 1200);

      // Clean up the fallback if user comes back quickly (means the app launched)
      const onVisChange = () => {
        if (document.hidden) {
          clearTimeout(fallbackTimer);
          document.removeEventListener("visibilitychange", onVisChange);
        }
      };
      document.addEventListener("visibilitychange", onVisChange);

      // Trigger the native handoff. Using location.href so the OS-level
      // protocol handler always gets first dibs — the browser does NOT
      // render a page; it just dispatches the URL to the OS.
      window.location.href = native;
      return;
    }
  }

  // Desktop or wa.me parse failure → external new tab.
  const opened = window.open(href, "_blank", "noopener,noreferrer");
  if (!opened) {
    try {
      // eslint-disable-next-line no-restricted-globals
      (window.top || window).location.href = href;
    } catch {
      window.location.href = href;
    }
  }
}
